import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Container, Row, Col, Button, Form, Badge, Spinner, Tooltip, OverlayTrigger, Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import ResizableTable from '../components/ResizableTable'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import StockItemInfo from '../components/StockItemInfo'
import { usePermissions } from '../contexts/PermissionContext'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'
import '../styles/selectControl.css'

function AmazonReportsScreen() {
    const { hasPermission } = usePermissions()

    // Zone 1 — Request Form
    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState('')
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [excludeInactive, setExcludeInactive] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    // Zone 2 — Queries Table
    const [queries, setQueries] = useState([])
    const [queriesLoading, setQueriesLoading] = useState(false)
    const autoRefreshRef = useRef(null)

    // Zone 3 — Economics Results
    const [activeQueryId, setActiveQueryId] = useState(null)
    const [economicsData, setEconomicsData] = useState([])
    const [economicsSummary, setEconomicsSummary] = useState(null)
    const [economicsLoading, setEconomicsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)

    const [exporting, setExporting] = useState(false)
    const [error, setError] = useState('')
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [activeQuickDate, setActiveQuickDate] = useState(null)

    const initialTableState = {
        visibility: {
            image: true,
            description: true,
            sku: true,
            asin: true,
            unitsSold: true,
            unitsReturned: true,
            revenue: true,
            refundedSales: true,
            netSales: true,
            avgSellingPrice: true,
            netUnitsSold: true,
            fbaFees: true,
            referralFees: true,
            otherFees: true,
            cogs: true,
            cogsPerUnit: true,
            netProceedsPerUnit: true,
            netProfit: true,
            margin: true,
        },
        widths: {}
    }

    const { columnState, updateColumnVisibility } = useTableState('amazon_economics', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility

    const toggleColumnVisibility = (columnId) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId])
    }

    const columnLabels = {
        image: 'Image',
        description: 'Description',
        sku: 'SKU',
        asin: 'ASIN',
        unitsSold: 'Units Sold',
        unitsReturned: 'Units Returned',
        revenue: 'Revenue',
        refundedSales: 'Refunded Sales',
        netSales: 'Net Sales',
        avgSellingPrice: 'Avg Selling Price',
        netUnitsSold: 'Net Units Sold',
        fbaFees: 'FBA Fees',
        referralFees: 'Referral Fees',
        otherFees: 'Other Fees',
        cogs: 'COGS',
        cogsPerUnit: 'COGS/Unit',
        netProceedsPerUnit: 'Net Proceeds/Unit',
        netProfit: 'Net Profit',
        margin: 'Margin %',
    }

    // Fetch accounts on mount
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                if (data) {
                    const active = data.filter(a => a.is_active)
                    setAccounts(active)
                    if (active.length === 1) {
                        setSelectedAccountId(active[0].id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch accounts:', err)
            }
        }
        fetchAccounts()
    }, [])

    // Fetch queries when account changes
    const fetchQueries = useCallback(async () => {
        if (!selectedAccountId) {
            setQueries([])
            return
        }
        setQueriesLoading(true)
        try {
            const data = await amazonAPI.queries.getAll(selectedAccountId)
            if (data) setQueries(data)
        } catch (err) {
            console.error('Failed to fetch queries:', err)
        } finally {
            setQueriesLoading(false)
        }
    }, [selectedAccountId])

    useEffect(() => {
        fetchQueries()
    }, [fetchQueries])

    // Auto-refresh when queries are in non-terminal states
    useEffect(() => {
        const nonTerminal = ['SUBMITTED', 'PROCESSING']
        const hasActive = queries.some(q => nonTerminal.includes(q.status))

        if (autoRefreshRef.current) {
            clearInterval(autoRefreshRef.current)
            autoRefreshRef.current = null
        }

        if (hasActive) {
            autoRefreshRef.current = setInterval(() => {
                fetchQueries()
            }, 30000)
        }

        return () => {
            if (autoRefreshRef.current) {
                clearInterval(autoRefreshRef.current)
            }
        }
    }, [queries, fetchQueries])

    const handleSubmitQuery = async () => {
        if (!selectedAccountId || !startDate || !endDate) return
        setSubmitting(true)
        setError('')
        try {
            const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
            await amazonAPI.queries.submit(selectedAccountId, {
                start_date: fmt(startDate),
                end_date: fmt(endDate),
                exclude_inactive: excludeInactive
            })
            fetchQueries()
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setSubmitting(false)
        }
    }

    const handleRetry = async (queryId) => {
        setError('')
        try {
            await amazonAPI.queries.retry(queryId)
            fetchQueries()
        } catch (err) {
            setError(formatErrorResponse(err))
        }
    }

    const handleDelete = async (query) => {
        if (!window.confirm('Delete this query and all its economics records?')) return
        setError('')
        try {
            await amazonAPI.queries.delete(query.id)
            if (activeQueryId === query.id) {
                setActiveQueryId(null)
                setEconomicsData([])
                setEconomicsSummary(null)
            }
            fetchQueries()
        } catch (err) {
            setError(formatErrorResponse(err))
        }
    }

    const handleViewResults = async (query) => {
        if (activeQueryId === query.id) {
            setActiveQueryId(null)
            setEconomicsData([])
            setEconomicsSummary(null)
            return
        }
        setActiveQueryId(query.id)
        setEconomicsLoading(true)
        setError('')
        try {
            const params = {
                account_id: query.seller_account,
                period_start: query.data_start_date,
                period_end: query.data_end_date
            }
            const [data, summary] = await Promise.all([
                amazonAPI.economics.getAll(params),
                amazonAPI.economics.getSummary(params)
            ])
            setEconomicsData(data || [])
            setEconomicsSummary(summary || null)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setEconomicsLoading(false)
        }
    }

    const handleExport = async (format) => {
        const query = queries.find(q => q.id === activeQueryId)
        if (!query) return
        setExporting(true)
        setError('')
        try {
            await amazonAPI.economics.export({
                account_id: query.seller_account,
                period_start: query.data_start_date,
                period_end: query.data_end_date,
            }, format)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setExporting(false)
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
        }, 300)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
    }

    const getStatusBadge = (status) => {
        const map = {
            SUBMITTED: 'warning',
            PROCESSING: 'warning',
            DONE: 'success',
            FAILED: 'danger'
        }
        return <Badge bg={map[status] || 'secondary'}>{status}</Badge>
    }

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    // Queries table columns
    const queryColumns = [
        {
            id: 'account',
            header: 'Account',
            cell: (row) => row.seller_account_name || ''
        },
        {
            id: 'dateRange',
            header: 'Date Range',
            cell: (row) => {
                const start = row.data_start_date ? formatDateOnly(row.data_start_date) : ''
                const end = row.data_end_date ? formatDateOnly(row.data_end_date) : ''
                return start && end ? `${start} – ${end}` : ''
            }
        },
        {
            id: 'status',
            header: 'Status',
            cell: (row) => getStatusBadge(row.status)
        },
        {
            id: 'submittedAt',
            header: 'Submitted At',
            cell: (row) => formatDateOnly(row.requested_at)
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (row) => (
                <>
                    {row.status === 'DONE' && (
                        <Button
                            variant={activeQueryId === row.id ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="me-2"
                            onClick={(e) => { e.stopPropagation(); handleViewResults(row) }}
                        >
                            {activeQueryId === row.id ? 'Hide Results' : 'View Results'}
                        </Button>
                    )}
                    {row.status === 'FAILED' && hasPermission('amazon.manage') && (
                        <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-2"
                            onClick={(e) => { e.stopPropagation(); handleRetry(row.id) }}
                        >
                            Retry
                        </Button>
                    )}
                    {['DONE', 'FAILED'].includes(row.status) && hasPermission('amazon.manage') && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDelete(row) }}
                        >
                            Delete
                        </Button>
                    )}
                </>
            )
        }
    ]

    // Economics table columns
    const handleEconomicsSort = (column, direction) => {
        setSortColumn(column)
        setSortDirection(direction)
    }

    const sortFieldMap = {
        description: (r) => r.stock_item_detail?.description || '',
        unitsSold: (r) => r.units_sold,
        unitsReturned: (r) => r.units_returned,
        revenue: (r) => Number(r.revenue),
        refundedSales: (r) => Number(r.refunded_sales || 0),
        netSales: (r) => Number(r.net_sales || 0),
        avgSellingPrice: (r) => Number(r.avg_selling_price || 0),
        netUnitsSold: (r) => r.net_units_sold || 0,
        fbaFees: (r) => Number(r.fba_fees),
        referralFees: (r) => Number(r.referral_fees),
        otherFees: (r) => Number(r.other_fees),
        cogs: (r) => Number(r.cogs),
        cogsPerUnit: (r) => r.units_sold > 0 ? Number(r.cogs) / r.units_sold : 0,
        netProceedsPerUnit: (r) => Number(r.net_proceeds_per_unit || 0),
        netProfit: (r) => Number(r.net_profit),
        margin: (r) => Number(r.margin_percent),
    }

    const filteredEconomics = economicsData.filter(row => {
        if (!searchTerm) return true
        const term = searchTerm.toLowerCase()
        return (
            (row.amazon_sku && row.amazon_sku.toLowerCase().includes(term)) ||
            (row.asin && row.asin.toLowerCase().includes(term)) ||
            (row.stock_item_detail?.description && row.stock_item_detail.description.toLowerCase().includes(term))
        )
    })

    const sortedEconomics = (() => {
        if (!sortColumn || !sortDirection || !sortFieldMap[sortColumn]) return filteredEconomics
        const getter = sortFieldMap[sortColumn]
        return [...filteredEconomics].sort((a, b) => {
            const va = getter(a)
            const vb = getter(b)
            if (typeof va === 'string') {
                const cmp = va.localeCompare(vb)
                return sortDirection === 'asc' ? cmp : -cmp
            }
            return sortDirection === 'asc' ? va - vb : vb - va
        })
    })()

    const economicsColumns = [
        columnVisibility.image && {
            id: 'image',
            header: 'Image',
            cell: (row) => row.stock_item_detail?.image ? (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip className="stock-item-tooltip">
                            <StockItemInfo stockItem={row.stock_item_detail} />
                        </Tooltip>
                    }
                >
                    <img
                        src={row.stock_item_detail.image}
                        alt="Stock Item Thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                    />
                </OverlayTrigger>
            ) : null
        },
        columnVisibility.description && { id: 'description', header: 'Description', sortable: true, cell: (row) => row.stock_item_detail?.description || '' },
        columnVisibility.sku && { id: 'sku', header: 'SKU', cell: (row) => row.amazon_sku },
        columnVisibility.asin && { id: 'asin', header: 'ASIN', cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.unitsSold && { id: 'unitsSold', header: 'Units Sold', sortable: true, cell: (row) => row.units_sold },
        columnVisibility.unitsReturned && { id: 'unitsReturned', header: 'Units Returned', sortable: true, cell: (row) => row.units_returned },
        columnVisibility.revenue && { id: 'revenue', header: 'Revenue', sortable: true, cell: (row) => formatCurrency(row.revenue) },
        columnVisibility.refundedSales && { id: 'refundedSales', header: 'Refunded Sales', sortable: true, cell: (row) => row.refunded_sales != null ? formatCurrency(row.refunded_sales) : '' },
        columnVisibility.netSales && { id: 'netSales', header: 'Net Sales', sortable: true, cell: (row) => row.net_sales != null ? formatCurrency(row.net_sales) : '' },
        columnVisibility.avgSellingPrice && { id: 'avgSellingPrice', header: 'Avg Selling Price', sortable: true, cell: (row) => row.avg_selling_price != null ? formatCurrency(row.avg_selling_price) : '' },
        columnVisibility.netUnitsSold && { id: 'netUnitsSold', header: 'Net Units Sold', sortable: true, cell: (row) => row.net_units_sold != null ? row.net_units_sold : '' },
        columnVisibility.fbaFees && { id: 'fbaFees', header: 'FBA Fees', sortable: true, cell: (row) => formatCurrency(row.fba_fees) },
        columnVisibility.referralFees && { id: 'referralFees', header: 'Referral Fees', sortable: true, cell: (row) => formatCurrency(row.referral_fees) },
        columnVisibility.otherFees && { id: 'otherFees', header: 'Other Fees', sortable: true, cell: (row) => formatCurrency(row.other_fees) },
        columnVisibility.cogs && { id: 'cogs', header: 'COGS', sortable: true, cell: (row) => formatCurrency(row.cogs) },
        columnVisibility.cogsPerUnit && { id: 'cogsPerUnit', header: 'COGS/Unit', sortable: true, cell: (row) => row.units_sold > 0 ? formatCurrency(Number(row.cogs) / row.units_sold) : '' },
        columnVisibility.netProceedsPerUnit && { id: 'netProceedsPerUnit', header: 'Net Proceeds/Unit', sortable: true, cell: (row) => row.net_proceeds_per_unit != null ? formatCurrency(row.net_proceeds_per_unit) : '' },
        columnVisibility.netProfit && { id: 'netProfit', header: 'Net Profit', sortable: true, cell: (row) => formatCurrency(row.net_profit) },
        columnVisibility.margin && {
            id: 'margin',
            header: 'Margin %',
            sortable: true,
            cell: (row) => row.margin_percent != null ? `${Number(row.margin_percent).toFixed(1)}%` : ''
        },
    ].filter(Boolean)

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-4">Amazon SKU Economics</h1>

            {error && <div className="alert alert-danger">{error}</div>}

            {/* Zone 1 — Request Form */}
            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-2">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => setSelectedAccountId(opt?.value || '')}
                            options={accountOptions}
                            placeholder="Select Account..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>Start Date</Form.Label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => { setStartDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            maxDate={endDate}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            portalId="root"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>End Date</Form.Label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => { setEndDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            maxDate={new Date()}
                            openToDate={startDate}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            portalId="root"
                        />
                    </div>
                    <div className="d-flex gap-1 align-items-center" style={{ paddingBottom: '4px' }}>
                        {[
                            { label: '1M', months: 1 },
                            { label: '3M', months: 3 },
                            { label: '6M', months: 6 },
                        ].map(({ label, months }) => (
                            <Badge
                                key={months}
                                bg={activeQuickDate === months ? 'primary' : 'light'}
                                style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => {
                                    const end = new Date()
                                    const start = new Date()
                                    start.setMonth(end.getMonth() - months)
                                    setStartDate(start)
                                    setEndDate(end)
                                    setActiveQuickDate(months)
                                }}
                            >
                                {label}
                            </Badge>
                        ))}
                    </div>
                    {hasPermission('amazon.manage') && (
                        <div>
                            <Button
                                variant="success"
                                onClick={handleSubmitQuery}
                                disabled={submitting || !selectedAccountId || !startDate || !endDate}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Query'
                                )}
                            </Button>
                        </div>
                    )}
                </div>
                <Form.Check
                    type="checkbox"
                    label="Exclude items with no activity"
                    checked={excludeInactive}
                    onChange={(e) => setExcludeInactive(e.target.checked)}
                    style={{ fontSize: '0.85rem' }}
                />
            </div>

            {/* Zone 2 — Queries Table */}
            {selectedAccountId && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">Query Requests</h4>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={fetchQueries}
                            disabled={queriesLoading}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                        </Button>
                    </div>
                    {queriesLoading && queries.length === 0 ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : queries.length === 0 ? (
                        <p className="text-muted">No queries found for this account.</p>
                    ) : (
                        <div className="table-responsive">
                            <ResizableTable
                                columns={queryColumns}
                                data={queries}
                                tableId="amazon_queries"
                                initialColumnWidths={{
                                    dateRange: 220,
                                    status: 120,
                                    submittedAt: 150,
                                    actions: 220
                                }}
                                minHeight="auto"
                                striped
                                hover
                                className="table-sm"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Zone 3 — Economics Results */}
            {activeQueryId && (
                <div className="mt-4">
                    <h4 className="mb-3">Economics Results</h4>

                    {economicsLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            {economicsSummary && (
                                <Row className="mb-4 g-3">
                                    <Col>
                                        <div className="bg-primary text-white p-3 rounded text-center">
                                            <h5 className="mb-1">Total Revenue</h5>
                                            <h3 className="mb-0">{formatCurrency(economicsSummary.total_revenue)}</h3>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="bg-success text-white p-3 rounded text-center">
                                            <h5 className="mb-1">Total Units Sold</h5>
                                            <h3 className="mb-0">{economicsSummary.total_units_sold ?? 0}</h3>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="bg-info text-white p-3 rounded text-center">
                                            <h5 className="mb-1">Net Profit</h5>
                                            <h3 className="mb-0">{formatCurrency(economicsSummary.total_net_profit)}</h3>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="bg-warning text-white p-3 rounded text-center">
                                            <h5 className="mb-1">Overall Margin</h5>
                                            <h3 className="mb-0">
                                                {economicsSummary.overall_margin != null
                                                    ? `${Number(economicsSummary.overall_margin).toFixed(1)}%`
                                                    : ''}
                                            </h3>
                                        </div>
                                    </Col>
                                    <Col>
                                        <div className="bg-secondary text-white p-3 rounded text-center">
                                            <h5 className="mb-1">SKU Count</h5>
                                            <h3 className="mb-0">{economicsSummary.sku_count ?? 0}</h3>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Export + Search */}
                            <div className="d-flex align-items-center mb-3">
                                <Button
                                    variant="success"
                                    onClick={() => handleExport('csv')}
                                    disabled={exporting}
                                    className="me-2"
                                >
                                    <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                                    Export CSV
                                </Button>
                                <Button
                                    variant="info"
                                    onClick={() => handleExport('excel')}
                                    disabled={exporting}
                                    className="me-2"
                                >
                                    <i className="bi bi-file-earmark-excel me-2"></i>
                                    Export Excel
                                </Button>
                                <div className="ms-2">
                                    {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                                </div>
                                <div className="ms-auto" style={{ maxWidth: '300px' }}>
                                    <SearchInputWithClear
                                        value={searchInputValue}
                                        onChange={handleSearch}
                                        onClear={handleClearSearch}
                                        placeholder="Search SKU or ASIN..."
                                    />
                                </div>
                            </div>

                            {/* Economics Table */}
                            {sortedEconomics.length === 0 ? (
                                <p className="text-muted">No economics data found.</p>
                            ) : (
                                <div className="table-responsive">
                                    <ResizableTable
                                        columns={economicsColumns}
                                        data={sortedEconomics}
                                        tableId="amazon_economics"
                                        enableSorting={true}
                                        onSort={handleEconomicsSort}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        initialColumnWidths={{
                                            sku: 150,
                                            asin: 130,
                                            unitsSold: 100,
                                            unitsReturned: 120,
                                            revenue: 120,
                                            refundedSales: 120,
                                            netSales: 120,
                                            avgSellingPrice: 130,
                                            netUnitsSold: 110,
                                            fbaFees: 110,
                                            referralFees: 120,
                                            otherFees: 110,
                                            cogs: 110,
                                            cogsPerUnit: 110,
                                            netProceedsPerUnit: 130,
                                            netProfit: 120,
                                            margin: 100
                                        }}
                                        striped
                                        hover
                                        className="table-sm"
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </Container>
    )
}

export default AmazonReportsScreen
